import classnames from 'classnames';
import React, { FC, useEffect, useReducer, useState, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';

import { ErrorMessage } from '@eika/attention-message';
import { ButtonTypes, StandardButton } from '@eika/button';
import '@eika/file-uploader-style';

import FileUploaderAttachmentRow from './FileUploaderAttachmentRow';
import FileUploaderIndicator from './FileUploaderIndicator';
import {
    Attachment,
    AttachmentActionType,
    AttachmentStatus,
    defaultOptions,
    mapFilesToAttachment,
    updateAttachments,
    FileUploaderOptions,
    RequiredFileUploaderOptions,
} from './file-uploader-types';
import FileValidation from './validation/FileValidation';
import { getWarnings, validateAttachmentsToUpload } from './validation/file-validation';
import defaultWarnings from './warnings/default-warnings';
import { Warnings } from './warnings/warning-types';

interface FileUploaderProps {
    uploadAttachment: (attachments: Attachment[]) => Promise<Attachment[]>;
    removeAttachment: (attachment: Attachment) => void;
    error?: string;
    options?: FileUploaderOptions;
}

const FileUploader: FC<FileUploaderProps> = ({ uploadAttachment, removeAttachment, error, options }) => {
    const fileUploaderOptions: RequiredFileUploaderOptions = useMemo(() => {
        return { ...defaultOptions, ...options };
    }, [options]);
    const DEFAULT_WARNINGS = useMemo(() => defaultWarnings(fileUploaderOptions), [fileUploaderOptions]);
    const [attachments, dispatch] = useReducer(updateAttachments, []);
    const [errors, setErrors] = useState<Warnings>(DEFAULT_WARNINGS);

    useEffect(() => {
        const uploaderrors = attachments.some(
            (f) => f.status === AttachmentStatus.ErrorTotalFileSize || f.status === AttachmentStatus.Error,
        );

        if (uploaderrors) {
            setErrors(getWarnings(fileUploaderOptions, attachments));
        }
    }, [attachments, fileUploaderOptions]);

    const clearErrors = () => {
        const failedAttachments = attachments.filter(
            (attachment) =>
                attachment.status === AttachmentStatus.Error ||
                attachment.status === AttachmentStatus.ErrorTotalFileSize,
        );

        if (failedAttachments.length) {
            dispatch({ type: AttachmentActionType.REMOVE_FAILED_ATTACHMENTS, payload: attachments });
        }
        setErrors(DEFAULT_WARNINGS);
    };

    const addAttachmantsToState = async (attachments: Attachment[]) =>
        new Promise((resolve) => {
            dispatch({ type: AttachmentActionType.ADD_ATTACHMENTS, payload: attachments });
            setTimeout(() => {
                resolve(0);
            }, 1);
        });

    const add = (chosenFiles: File[]): void => {
        const attachmentsToUpload: Attachment[] = mapFilesToAttachment(chosenFiles);
        setErrors(getWarnings(fileUploaderOptions, attachments, attachmentsToUpload, true));

        const validAttachments: Attachment[] = validateAttachmentsToUpload(
            attachmentsToUpload,
            attachments,
            fileUploaderOptions,
        );

        if (validAttachments.length) {
            addAttachmantsToState(validAttachments).then(() => {
                uploadAttachment(validAttachments)
                    .then((attachments) =>
                        dispatch({ type: AttachmentActionType.UPDATE_ATTACHMENT_STATUS, payload: attachments }),
                    )
                    .catch(() =>
                        dispatch({
                            type: AttachmentActionType.UPDATE_ATTACMENTS_ON_FAILURE,
                            payload: validAttachments,
                        }),
                    );
            });
        }
    };

    const remove = (attachment: Attachment) => {
        dispatch({ type: AttachmentActionType.REMOVE_ATTACHMENT, payload: [attachment] });
        setErrors(DEFAULT_WARNINGS);
        removeAttachment(attachment);
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop: add });

    return (
        <div className="sgw-file-uploader">
            <div
                className={classnames(
                    { 'file-uploader--dragActive': isDragActive },
                    { 'file-uploader--dragNotActive': !isDragActive },
                    'file-uploader',
                    fileUploaderOptions.className,
                )}
                {...getRootProps({
                    onClick: clearErrors,
                })}
            >
                <input {...getInputProps()} />
                <div className="file-uploader__dropzone">
                    <span className="file-uploader__dropzone__buttons">
                        <p>{fileUploaderOptions.dropAreaText}</p>
                        <StandardButton type="button" buttonType={ButtonTypes.STANDARD_NEGATIVE}>
                            {fileUploaderOptions.dropButtonLabel}
                        </StandardButton>
                    </span>
                </div>
            </div>
            {attachments && attachments.length > 0 && (
                <FileUploaderIndicator
                    attachments={attachments}
                    maxCombinedUploadByteSize={fileUploaderOptions.maxCombinedUploadByteSize}
                />
            )}
            {errors && <FileValidation warnings={errors} />}
            {attachments && attachments.length > 0 && (
                <>
                    <h3 className="typography-h2">{fileUploaderOptions.attachmentListHeading}</h3>
                    <ul className="file-uploader-list">
                        {attachments.map((attachment) => (
                            <FileUploaderAttachmentRow
                                key={attachment.id}
                                file={attachment}
                                status={attachment.status}
                                onDelete={() => remove(attachment)}
                            />
                        ))}
                    </ul>
                </>
            )}
            {error && <ErrorMessage text={error} />}
        </div>
    );
};

export default FileUploader;
